import axios from 'axios';
import { EnrollService } from '@/services/enroll.service';
import { TermsOfUseService } from '@/services/termsOfUse.service';
import { offlineStorage } from '@/services/offline/offlineStorage';
import { imageCache } from '@/services/offline/imageCache';
import { offlineEventQueue, OfflineEventType } from '@/services/offlineEventQueue';
import { pushOfflineSessions } from '@/services/offline/vdplayerPositionSync';
import { Capacitor } from '@capacitor/core';
import Vue from 'vue';
import { reject } from 'lodash';
import $router from '@/router'
import { maybeAskPushPermission, PROMPT_TRIGGERS } from '@/services/push/pushPrompt';

// Poster default (icone por tipo) para o "continuar de onde parou".
function getLastAccessDefaultImage(data) {
  if(data.learningObjectType == 'Pdf')
    return require('@/assets/images/posters/pdf.png');
  if(data.learningObjectType == 'HtmlContent')
    return require('@/assets/images/posters/texto.png');
  if(data.learningObjectType == 'ExternalContent')
    return require('@/assets/images/posters/oferta.png');
  if(data.learningObjectType == 'BdQ') {
    switch(data.formType) {
      case 'Proof':
      case 'Simulated':
        return require('@/assets/images/posters/proof-simulated.png');
      case 'SurveyResearch':
        return require('@/assets/images/posters/surveyresearch.png');
      case 'Exercise':
        return require('@/assets/images/posters/exercise.png');
    }
  }
  return '';
}

// 5312: a capa da aula e servida em URL FIXA ({app}/learning-objects/{loId}.png) com
// Cache-Control max-age de 1 ano. Sem cache-buster, o navegador do aluno "aprovaria" no
// probe a imagem antiga do proprio cache mesmo depois de o produtor substituir/restaurar.
// Bucket de 2 min: a troca propaga em ate ~2 min (piso = purge do CDN, ~1 min) e, dentro
// da janela, navegador e CDN continuam cacheando normalmente (1 request por imagem por
// reload da pagina, nao por acesso).
function imageCacheBuster(url) {
  if (!url) return url;
  // So a URL DERIVADA pela API atual (.../learning-objects/{id}.png, sem sufixo) precisa
  // de buster. Quando a API persistir o campo (plano B 5312), o valor volta como URL unica
  // por upload ({id}-{ts}.{ext}) — imutavel, pode cachear normalmente — ou '' (restaurada).
  if (!/\/learning-objects\/\d+\.png$/.test(url)) return url;
  var bucket = Math.floor(Date.now() / 120000); // muda a cada 2 minutos
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'x=' + bucket;
}

// Guard de reentrância do flushOfflineEventQueue (módulo-level, persiste entre dispatches).
let _flushing = false;
// Lote do offline-sessions. DEVE ser <= MaxOfflineItemsPerRequest do endpoint
// (BroadcastsController), senão o servidor responde 400 e o flush inteiro conta
// falha até envenenar a fila. Mantido em 20 (era 100) porque o endpoint processa
// os itens SEQUENCIALMENTE, com 2 conexões SQL + 3 round-trips Mongo cada — 100
// itens seguravam a conexão por ~7,5s e poucos requests concorrentes esgotavam o
// pool, afetando todo o playback da plataforma. Mais requests menores é irrelevante
// aqui (o flush é background).
const OFFLINE_SESSION_BATCH = 20;
// Após N falhas de RESPOSTA (4xx/5xx) o evento é descartado (poison) em vez de re-tentar
// pra sempre. Falha de rede/transporte não conta (transitória).
const OFFLINE_EVENT_MAX_ATTEMPTS = 5;

function _cacheEnrollImages(enrolls) {
  const urls = [];
  enrolls.forEach(e => {
    if (e.image) urls.push(e.image);
    if (e.logo) urls.push(e.logo);
  });
  if (!urls.length) return;

  Promise.all(urls.map(url => imageCache.cacheImage(url)))
    .then(localUrls => {
      const map = {};
      urls.forEach((url, i) => { if (localUrls[i]) map[url] = localUrls[i]; });
      if (Object.keys(map).length) offlineStorage.mergeImageMap(map);
    });
}

export default {
  namespaced: true,
  state: {
    enrolls: [],
    // true só após carga de rede bem-sucedida. False (boot/em-voo/cache) = lista
    // não autoritativa pra inferir revogação (vide download/downloadedCourses).
    enrollsFresh: false,
    filter: null,
    headerSearch: null,
    current: null,
    currentClass: null,
    currentLearningObjects: null,
    currentLearningObject: null,
    lastAccess: null,
    inFullscreen: false,
    isPreview: false,

    termsOfUseStatus: {},

    // Telemetria do flush da fila offline (consumida pelo painel dev OfflineSyncDebug).
    offlineSync: { active: false, total: 0, done: 0 },
  },
  actions: {
    setFullscreen({ commit }, value) {
      commit('SET_FULLSCREEN', value);
    },
    // `awaitEntitlements`: encadeia o syncEntitlements no retorno em vez de
    // soltá-lo. Só o fluxo de desbloqueio manual usa — ele precisa LER o
    // resultado do sync logo depois; a navegação normal segue sem esperar.
    getUserEnroll ({ commit }, payload) {
      // Payload tolerante a null: vários dispatch antigos mandam `null`, e
      // desestruturação com default só cobre `undefined`.
      const awaitEntitlements = !!(payload && payload.awaitEntitlements);
      if (Capacitor.isNativePlatform() && !this.state.dom.isConnected) {
        const cached = offlineStorage.getEnrolls();
        if (cached) {
          commit('SET_ENROLLS', cached);
        }
        // Cache não é autoritativo — só lista fresca de rede infere revogação.
        commit('SET_ENROLLS_FRESH', false);
        return Promise.resolve();
      }
      return EnrollService.getUserEnroll()
        .then(response => {
          commit('SET_ENROLLS', response.data);
          commit('SET_ENROLLS_FRESH', true);
          // Entitlement do lease offline: a lista FRESCA de rede é a fonte de
          // "tem acesso" (o backend não emite 402). Revoga o lease dos cursos
          // bloqueados (pendingPayment/blocked/temporarilyDisabled) e renova os
          // liberados. `total` (maior dos contadores) deixa o syncEntitlements
          // detectar lista truncada pela paginação e não revogar em falso.
          // `httpStatus` distingue lista vazia AUTORITATIVA (204: o backend
          // responde NoContent quando o aluno não tem matrícula nenhuma, que é
          // justamente o sinal de acesso revogado) de vazio por outro motivo.
          // Fire-and-forget por padrão — não trava a navegação.
          const entitlementSync = this.dispatch('lease/syncEntitlements', {
            items: (response.data && response.data.items) || [],
            total: response.data
              ? Math.max(Number(response.data.totalRows) || 0, Number(response.data.totalItems) || 0, Number(response.data.totalFiltred) || 0)
              : 0,
            httpStatus: response.status
          }).catch(() => {});
          if (Capacitor.isNativePlatform()) {
            offlineStorage.saveEnrolls(response.data);
            _cacheEnrollImages((response.data && response.data.items) || []);
          }
          if (awaitEntitlements) return entitlementSync;
        })
        .catch(error => {
          if (Capacitor.isNativePlatform()) {
            const cached = offlineStorage.getEnrolls();
            if (cached) {
              commit('SET_ENROLLS', cached);
              commit('SET_ENROLLS_FRESH', false);
              return;
            }
          }
          commit('toast/NEW', { type: 'error', message: error.message, suppressOffline: true }, { root: true });
          reject(false);
        })
    },
    setLastAccess({ commit }, {enroll, learningObject}) {
      EnrollService.setLastAccess(enroll.id, enroll.courseId, learningObject.moduleId, learningObject.disciplineId, learningObject.classId, learningObject.id)
        .then(response => this.dispatch('enroll/getLastAccess', enroll.id));
    },
    setLastAccessNextContent({commit}, {enroll, learningObjectNextId}) {
      const learningObject = this.state.enroll.currentLearningObjects.find(x => x.id == learningObjectNextId);
      EnrollService.setLastAccess(enroll.id, enroll.courseId, learningObject.moduleId, learningObject.disciplineId, learningObject.classId, learningObject.id)
        .then(response => this.dispatch('enroll/getLastAccess', enroll.id));
    },
    getLastAccess({ commit }, enrollId) {
      // navigator.onLine não confiável no WebView Android; usar o estado do Capacitor Network
      if (Capacitor.isNativePlatform() && !this.state.dom.isConnected) return Promise.resolve();
      return EnrollService.getLastAccess(enrollId)
          .then(response => {
            var data = response.data;
            var defaultImage = getLastAccessDefaultImage(data);

            // Tipos de arquivo: a API sempre devolve uma URL em learningObjectImage, mas ela
            // pode nao existir (404) quando nao ha imagem personalizada. Exibimos o default
            // do tipo e, se a imagem carregar, trocamos por ela. Audio/video (defaultImage
            // vazio) mantem a URL retornada pela API.
            if(defaultImage) {
              var apiImage = data.learningObjectImage;
              data.learningObjectImage = defaultImage;
              if(apiImage) {
                var probeUrl = imageCacheBuster(apiImage);
                var probe = new Image();
                probe.onload = function() { data.learningObjectImage = probeUrl; };
                probe.src = probeUrl;
              }
            }

            commit('SET_LAST_ACCESS', data);
          })
        .catch(error => commit('toast/NEW', { type: 'error', message: error.message, suppressOffline: true }, { root: true }));
    },
    presetCurrentEnroll({ commit }, { enrollId }) {
      var enroll = this.state.enroll.enrolls.find(x => x.id == enrollId);
      commit("SET_CURRENT_ENROLL", { enroll, learningObjects: [] });
    },
    setCurrentEnroll({ commit }, { enrollId, preview }) {
      if (!preview && enrollId && Capacitor.isNativePlatform() && !this.state.dom.isConnected) {
        const cached = offlineStorage.getGrid(enrollId);
        if (cached) {
          const enroll = this.state.enroll.enrolls?.find(x => x.id == enrollId);
          if (enroll) {
            axios.defaults.headers.common['X-ApplicationId'] = enroll.applicationId;
          }
          // reutiliza o mesmo fluxo de processamento do full-grid
          return this.dispatch('enroll/setCurrentEnroll', { enrollId: null, preview: cached });
        }
      }

      var getModulesClasses = null;
      var applicationUserId = null;
      if (enrollId) {
        commit('SET_LAST_ACCESS', null);

        var enroll = this.state.enroll.enrolls?.find(x => x.id == enrollId);
        if (enroll) {
          axios.defaults.headers.common["X-ApplicationId"] = enroll.applicationId;
          applicationUserId = enroll.applicationUserId;
        }

        getModulesClasses = EnrollService.getModulesClasses(enroll.id);
      } else {
        getModulesClasses = Promise.resolve({
          data: preview
        });
      }

      getModulesClasses.then(response => {
          var enroll = response.data;
          enroll.applicationUserId = applicationUserId;

          if(!enroll) throw { message: 'Ocorreu uma falha. Por favor, tente novamente. Caso o erro persista, entre em contato com o suporte.' }

          var learningObjects = [];
          var structureType = enroll.structureType.toLowerCase();

          // Poster default (icone por tipo) usado quando a imagem da API nao existe (404).
          var getDefaultImage = function(learningObj) {
            if (learningObj.htmlContent)
              return require('@/assets/images/posters/texto.png');
            if (learningObj.externalContent)
              return require('@/assets/images/posters/oferta.png');
            if(learningObj.pdf)
              return require('@/assets/images/posters/pdf.png');
            if(learningObj.forms){
              switch(learningObj.forms.type) {
                case 'Proof':
                case 'Simulated':
                  return require('@/assets/images/posters/proof-simulated.png');
                case 'SurveyResearch':
                  return require('@/assets/images/posters/surveyresearch.png');
                case 'Exercise':
                  return require('@/assets/images/posters/exercise.png');
              }
            }
            return '';
          };

          // Story-1721 (fix): guarda a apiImage crua de cada learningObject na
          // primeira vez que passa por getLearningObjectImage. currentLearningObject.image
          // e sobrescrito de forma sincrona com o poster default logo abaixo, entao uma
          // segunda chamada sobre o mesmo objeto (previousImage/nextImage) nao pode mais
          // ler a URL real em learningObj.image — precisa vir deste cache.
          var rawApiImageById = new Map();

          // Story-1721 (fix): getLearningObjectImage e chamada de novo sobre o mesmo
          // learningObj ao montar previousImage/nextImage do vizinho. Nessa segunda
          // chamada, o onload so pode corrigir learningObj.image (a aula em si) — nao
          // alcanca a propriedade previousImage/nextImage do OUTRO objeto que a leu.
          // target/targetKey deixam o caller dizer onde a atualizacao assincrona deve
          // cair quando o campo de destino nao e o proprio learningObj.image.
          var getLearningObjectImage = function(learningObj, target, targetKey) {
            // Video e audio usam sempre a imagem retornada pela API.
            if(learningObj.broadcast)
              return learningObj.image;
            if(learningObj.audio)
              return learningObj.image;

            var defaultImage = getDefaultImage(learningObj);
            var apiImage = rawApiImageById.has(learningObj.id)
              ? rawApiImageById.get(learningObj.id)
              : learningObj.image;
            rawApiImageById.set(learningObj.id, apiImage);

            // A API sempre devolve uma URL em image, mas ela pode nao existir (404) quando
            // nao ha imagem personalizada. Exibimos o poster default do tipo e, se a imagem
            // carregar (existe), trocamos por ela. learningObj e o mesmo objeto reativo do
            // store, entao o onload propaga aos componentes.
            if(apiImage) {
              var probeUrl = imageCacheBuster(apiImage);
              var probe = new Image();
              probe.onload = function() {
                learningObj.image = probeUrl;
                if (target && targetKey) target[targetKey] = probeUrl;
              };
              probe.src = probeUrl;
            }

            return defaultImage;
          };

          switch (structureType) {
            case "modulesanddisciplines":
              for (var ixModule = 0, lenModules = (enroll.modules || []).length; ixModule < lenModules; ixModule++) {
                var currentModule = enroll.modules[ixModule];

                for (var ixDiscipline = 0, lenDisciplines = (currentModule.disciplines || []).length; ixDiscipline < lenDisciplines; ixDiscipline++) {
                  var currentDiscipline = currentModule.disciplines[ixDiscipline];

                  for (var ixClass = 0, lenClasses = (currentDiscipline.classes || []).length; ixClass < lenClasses; ixClass++) {
                    var currentClass = currentDiscipline.classes[ixClass];
                    currentClass.isPreview = enroll.isPreview;

                    for (var ixLearningObject = 0, lenLearningObjects = (currentClass.learningObjects || []).length; ixLearningObject < lenLearningObjects; ixLearningObject++) {
                      var currentLearningObject = currentClass.learningObjects[ixLearningObject];

                      currentLearningObject.moduleId = currentModule.id;
                      currentLearningObject.disciplineId = currentDiscipline.id;
                      currentLearningObject.category = currentClass.title;
                      currentLearningObject.state = enroll.isPreview ? null : currentLearningObject.state;
                      currentLearningObject.image = getLearningObjectImage(currentLearningObject);

                      learningObjects.push(currentLearningObject);
                    }

                  }

                }
              }
              break;
            case "modules":
              for (var ixModule = 0, lenModules = (enroll.modules || []).length; ixModule < lenModules; ixModule++) {
                var currentModule = enroll.modules[ixModule];
                for (var ixClass = 0, lenClasses = (currentModule.classes || []).length; ixClass < lenClasses; ixClass++) {
                  var currentClass = currentModule.classes[ixClass];
                  currentClass.isPreview = enroll.isPreview;
                  for (var ixLearningObject = 0, lenLearningObjects = (currentClass.learningObjects || []).length; ixLearningObject < lenLearningObjects; ixLearningObject++) {
                    var currentLearningObject = currentClass.learningObjects[ixLearningObject];

                    currentLearningObject.moduleId = currentModule.id;
                    currentLearningObject.category = currentClass.title;
                    currentLearningObject.isPreview = enroll.isPreview;
                    currentLearningObject.image = getLearningObjectImage(currentLearningObject);

                    if(currentClass.state == 'Unpublished' || (currentClass.state == 'Scheduled' && currentClass.releasedDate)) {
                      currentLearningObject.state = currentClass.state;
                      currentLearningObject.releasedDate = currentClass.releasedDate;
                    }
                    learningObjects.push(currentLearningObject);
                  }
                }
              }
              break;
            case "disciplines":
              for (var ixDiscipline = 0, lenDisciplines = (enroll.disciplines || []).length; ixDiscipline < lenDisciplines; ixDiscipline++) {
                var currentDiscipline = enroll.disciplines[ixDiscipline];

                for (var ixClass = 0, lenClasses = (currentDiscipline.classes || []).length; ixClass < lenClasses; ixClass++) {
                  var currentClass = currentDiscipline.classes[ixClass];
                  currentClass.isPreview = enroll.isPreview;

                  for (var ixLearningObject = 0, lenLearningObjects = (currentClass.learningObjects || []).length; ixLearningObject < lenLearningObjects; ixLearningObject++) {
                    var currentLearningObject = currentClass.learningObjects[ixLearningObject];

                    currentLearningObject.disciplineId = currentDiscipline.id;
                    currentLearningObject.category = currentClass.title;
                    currentLearningObject.isPreview = enroll.isPreview;
                    currentLearningObject.image = getLearningObjectImage(currentLearningObject);

                    if(currentClass.state == 'Unpublished' || (currentClass.state == 'Scheduled' && currentClass.releasedDate)) {
                      currentLearningObject.state = currentClass.state;
                      currentLearningObject.releasedDate = currentClass.releasedDate;
                    }
                    learningObjects.push(currentLearningObject);
                  }
                }
              }
              break;
            case "classes":
              for (var ixClass = 0, lenClasses = (enroll.classes || []).length; ixClass < lenClasses; ixClass++) {
                var currentClass = enroll.classes[ixClass];
                currentClass.isPreview = enroll.isPreview;
                for (var ixLearningObject = 0, lenLearningObjects = (currentClass.learningObjects || []).length; ixLearningObject < lenLearningObjects; ixLearningObject++) {
                  var currentLearningObject = currentClass.learningObjects[ixLearningObject];
                  currentLearningObject.category = currentClass.title;
                  currentLearningObject.isPreview = enroll.isPreview;
                  currentLearningObject.image = getLearningObjectImage(currentLearningObject);

                  if(currentClass.state == 'Unpublished' || (currentClass.state == 'Scheduled' && currentClass.releasedDate)) {
                    currentLearningObject.state = currentClass.state;
                    currentLearningObject.releasedDate = currentClass.releasedDate;
                  }
                  learningObjects.push(currentLearningObject);
                }
              }
              break;
          }

          var releasedLearningObjects = (learningObjects || []).filter(lo => { return lo.state != 'Unpublished' && (lo.state != 'Scheduled' || !lo.releasedDate) });

          for (var i = 0; i < releasedLearningObjects.length; i++) {
            if (i > 0) {
              releasedLearningObjects[i].previousIndex = i - 1;
              releasedLearningObjects[i].previousId = releasedLearningObjects[i - 1].id;
              releasedLearningObjects[i].previousName = releasedLearningObjects[i - 1].title;
              releasedLearningObjects[i].previousImage = getLearningObjectImage(releasedLearningObjects[i - 1], releasedLearningObjects[i], 'previousImage');
            }

            if (i < releasedLearningObjects.length - 1) {
              releasedLearningObjects[i].nextIndex = i + 1;
              releasedLearningObjects[i].nextId = releasedLearningObjects[i + 1].id;
              releasedLearningObjects[i].nextName = releasedLearningObjects[i + 1].title;
              releasedLearningObjects[i].nextImage = getLearningObjectImage(releasedLearningObjects[i + 1], releasedLearningObjects[i], 'nextImage');
            }
          }

          commit("SET_CURRENT_ENROLL", { enroll, learningObjects });

          // Curso carregado FRESCO do servidor (online, não-preview). Se ele já tem
          // downloads offline, re-cacheia a estrutura: atualiza o snapshot salvo
          // (structure.json) com o grid fresco — descrição/título/metadados que o
          // produtor mudou — e reconcilia órfãos (vídeo trocado → loId novo).
          // Sem isso, só BAIXAR reescrevia o snapshot; apenas VER o curso online não,
          // então offline aparecia a versão antiga dos metadados.
          // Gate em isStructureCached: não criar dados offline pra curso sem download
          // (cacheEnrollStructure marca SET_CACHED + salva grid). `!preview && enrollId`
          // garante a carga autoritativa (preview/offline passa enrollId=null).
          if (!preview && enrollId && Capacitor.isNativePlatform()
              && this.getters['download/isStructureCached'](enrollId)) {
            this.dispatch('download/cacheEnrollStructure', { enrollId });
          }
        })
        .catch(async error => {
          await $router.push("/p/my-courses");
          await commit('toast/NEW', { type: 'error', message: error.message, suppressOffline: true }, { root: true });
        });

      var getLastAccess = null;
      if (preview) {
        getLastAccess = Promise.resolve(null);
      } else {
        getLastAccess = this.dispatch('enroll/getLastAccess', enroll.id);
      }

      return Promise.all([ getModulesClasses, getLastAccess ]);
    },
    clearCurrentEnroll({commit}) {
      commit('CLEAR_CURRENT');
    },
    updateFilter({ commit }, filter) {
      commit('UPDATE_FILTER', filter);
    },
    setCurrentLearningObject({ commit }, learningObjectId) {
      var selectedLearningObject = this.state.enroll.currentLearningObjects.find(x => x.id == learningObjectId);
      commit('SET_CURRENT_LEARNING_OBJECT', selectedLearningObject);
    },
    setProgress({ dispatch, commit }, payload) {
      // Aula concluída = um dos dois momentos do pedido contextual de push ("1ª
      // aula concluída"). Fica fora a conclusão AUTOMÁTICA de PDF/texto, que a
      // StudentClass dispara só por abrir a aula — pedir ali seria interromper
      // quem acabou de chegar. Regras de quando pedir: services/push/pushPrompt.
      if (payload && payload.progress === 100 && Capacitor.isNativePlatform()) {
        const lo = this.state.enroll.currentLearningObject;
        const autoCompleted = lo && String(lo.id) === String(payload.learningObjectId) && (lo.pdf || lo.htmlContent);
        if (!autoCompleted) maybeAskPushPermission(PROMPT_TRIGGERS.LESSON);
      }
      if (Capacitor.isNativePlatform() && !this.state.dom.isConnected) {
        // studentId carimba o dono: a fila sobrevive ao logout e o flush só envia
        // o que é do aluno logado (device compartilhado).
        offlineEventQueue.enqueue(
          OfflineEventType.ENROLL_PROGRESS,
          payload,
          this.state.user && this.state.user.currentUser ? this.state.user.currentUser.studentId : null
        );
        commit('SET_PROGRESS', payload.progress);
        commit('SET_OBJ_PROGRESS', {
          classId: payload.classId,
          learningObjectId: payload.learningObjectId,
          progress: payload.progress
        });
        return Promise.resolve();
      }
      // Callers normais não devem ver a rejeição (o comportamento original
      // engolia o erro). O flush usa _postProgress direto pra saber o resultado.
      return dispatch('_postProgress', payload).catch(() => {});
    },
    // POST real do progresso. Lança em falha — usado pelo flush pra decidir se
    // remove o evento da fila (remove-on-success).
    _postProgress({ commit }, { enrollId, courseId, moduleId, disciplineId, classId, learningObjectId, broadcastId, progress }) {
      return EnrollService.setProgress(enrollId, courseId, moduleId, disciplineId, classId, learningObjectId, broadcastId, progress)
        .then(response => {
          commit('SET_PROGRESS', progress);
          if (response.data) {
            commit('SET_OBJ_PROGRESS', { classId, learningObjectId, progress });
          }
        });
    },
    // Reenvia os eventos enfileirados offline ao reconectar. Remove-on-success:
    // cada evento só sai da fila quando o POST confirma; em falha fica pra próxima
    // tentativa (próximo reconnect). Idempotente — reenviar `complete` é seguro.
    async flushOfflineEventQueue({ dispatch, commit, rootGetters, rootState }) {
      // Guard de reentrância: rede oscilando dispara flushes concorrentes que corrompem a fila.
      if (_flushing) return;
      _flushing = true;
      // Logs do offline-sync ligados pela mesma config do painel de diagnóstico.
      const debug = !!rootGetters['download/getShowOfflineSyncPanel'];
      let done = 0;
      const bump = (n) => { done += n; commit('SET_OFFLINE_SYNC', { done }); };
      try {
        // Só sobe o que é do aluno LOGADO. A fila sobrevive ao logout, então num
        // device compartilhado os eventos do aluno anterior seriam enviados com o
        // JWT do atual (progresso alheio marcado como concluído). Os eventos de
        // outra conta FICAM na fila — quando o dono logar de novo, sobem certos;
        // limpar no logout resolveria o vazamento mas descartaria dado legítimo.
        const currentStudentId = rootState.user && rootState.user.currentUser
          ? rootState.user.currentUser.studentId
          : null;
        if (!currentStudentId) return;

        // Reivindicação de evento órfão (sem carimbo de dono). Só sobe o que o
        // próprio payload prova ser do aluno logado; o resto fica na fila.
        const currentEmail = rootState.user.currentUser.email
          ? String(rootState.user.currentUser.email).trim().toLowerCase()
          : null;
        // Lista de matrículas do logado. Vazia (offline no boot, lista ainda não
        // buscada) = não dá pra provar nada, e aí nenhum órfão é reivindicado —
        // que é o lado seguro: fica pra um flush futuro.
        const myEnrollIds = new Set(
          Array.from(rootState.enroll.enrolls || [])
            .filter(e => e && e.id != null)
            .map(e => String(e.id))
        );
        const canClaimOrphan = (ev) => {
          const p = ev && ev.payload;
          if (!p) return false;
          // Progresso carrega a matrícula: é do logado se estiver na lista dele.
          if (ev.type === OfflineEventType.ENROLL_PROGRESS) {
            return p.enrollId != null && myEnrollIds.has(String(p.enrollId));
          }
          // Sessão offline carrega o e-mail de quem assistiu.
          if (ev.type === OfflineEventType.OFFLINE_SESSION) {
            return !!currentEmail && !!p.userEmail &&
              String(p.userEmail).trim().toLowerCase() === currentEmail;
          }
          return false;
        };

        const events = offlineEventQueue.ownedBy(currentStudentId, canClaimOrphan);
        commit('SET_OFFLINE_SYNC', { active: true, total: events.length, done: 0 });

        // Sessões offline → vdplayer, em lote por (customer, usuário). Antes do laço de
        // progresso pra não cair no descarte de "tipo desconhecido". remove-on-success.
        const sessionEvents = events.filter(e => e.type === OfflineEventType.OFFLINE_SESSION);
        if (sessionEvents.length) {
          const groups = {};
          for (const ev of sessionEvents) {
            const p = ev.payload || {};
            if (!p.customerId || !p.token || !p.userEmail || !p.broadcastId) {
              offlineEventQueue.remove(ev.id); // payload incompleto/legado — descarta
              bump(1);
              continue;
            }
            // Agrupa por (customer, usuário), não por token — token rotacionado vira 1 POST só.
            const key = `${p.customerId}::${p.userEmail}`;
            if (!groups[key]) groups[key] = { customerId: p.customerId, userEmail: p.userEmail, token: p.token, evs: [] };
            groups[key].token = p.token; // último enfileirado = token mais novo
            groups[key].evs.push(ev);
          }
          for (const g of Object.values(groups)) {
            // Chunk pra não estourar o cap do endpoint; remove-on-success por chunk.
            for (let i = 0; i < g.evs.length; i += OFFLINE_SESSION_BATCH) {
              const slice = g.evs.slice(i, i + OFFLINE_SESSION_BATCH);
              const items = slice.map(ev => ({
                broadcastId: ev.payload.broadcastId,
                position: ev.payload.position,
                watchedAt: ev.payload.watchedAt
              }));
              const { status, retryableIndexes } = await pushOfflineSessions(g.userEmail, items, g.token, g.customerId, debug);
              if (status >= 200 && status < 300) {
                // Sucesso parcial (207): o servidor grava por item e sinaliza os que
                // falharam por causa transitória (Mongo/SQL fora). Remover o chunk
                // inteiro aqui apagaria o progresso do aluno em silêncio — então os
                // reenviáveis ficam na fila contando tentativa (poison depois de N).
                const keep = slice.filter((_, idx) => retryableIndexes.has(idx));
                const settled = slice.filter((_, idx) => !retryableIndexes.has(idx));
                if (settled.length) {
                  offlineEventQueue.removeMany(settled.map(ev => ev.id));
                  bump(settled.length);
                }
                if (keep.length) {
                  const dropped = offlineEventQueue.recordFailures(keep.map(ev => ev.id), OFFLINE_EVENT_MAX_ATTEMPTS);
                  if (dropped.length) {
                    console.warn(`[offlineEventQueue] ${dropped.length} sessão(ões) offline descartada(s) após ${OFFLINE_EVENT_MAX_ATTEMPTS} tentativas (falha recuperável no servidor)`);
                    bump(dropped.length);
                  }
                }
              } else if (status > 0) {
                // Servidor respondeu erro (token inválido, broadcast ruim, etc.) → conta
                // tentativa e descarta poison após N. status 0 (rede) cai fora: mantém.
                const dropped = offlineEventQueue.recordFailures(slice.map(ev => ev.id), OFFLINE_EVENT_MAX_ATTEMPTS);
                if (dropped.length) {
                  console.warn(`[offlineEventQueue] ${dropped.length} sessão(ões) offline descartada(s) após ${OFFLINE_EVENT_MAX_ATTEMPTS} tentativas (HTTP ${status})`);
                  bump(dropped.length);
                }
              }
            }
          }
        }

        // Progresso (conclusão) → EAD.
        for (const ev of events) {
          if (ev.type === OfflineEventType.OFFLINE_SESSION) continue; // já tratado acima
          if (ev.type !== OfflineEventType.ENROLL_PROGRESS) {
            offlineEventQueue.remove(ev.id); // tipo desconhecido — descarta
            bump(1);
            continue;
          }
          try {
            await dispatch('_postProgress', ev.payload);
            offlineEventQueue.remove(ev.id);
            bump(1);
          } catch (e) {
            // Só falha de RESPOSTA (axios error.response) conta tentativa; rede não.
            const status = e?.response?.status || 0;
            if (status > 0) {
              const dropped = offlineEventQueue.recordFailures([ev.id], OFFLINE_EVENT_MAX_ATTEMPTS);
              if (dropped.length) {
                console.warn(`[offlineEventQueue] progresso descartado após ${OFFLINE_EVENT_MAX_ATTEMPTS} tentativas (HTTP ${status})`);
                bump(1);
              }
            } else {
              // Rede/transporte → mantém pro próximo reconnect, sem queimar tentativa.
              console.warn('[offlineEventQueue] reenvio de progresso falhou (rede), mantendo na fila:', e?.message || e);
            }
          }
        }
      } finally {
        _flushing = false;
        commit('SET_OFFLINE_SYNC', { active: false });
      }
    },
    refreshProgress({ commit }, { enrollId, courseId, moduleId, disciplineId, classId, learningObjectId, broadcastId }) {
      EnrollService.refreshProgress(enrollId, courseId, moduleId, disciplineId, classId, learningObjectId, broadcastId)
      .then(response => {
        if(response && response.data) {
          commit('SET_OBJ_PROGRESS', {classId, learningObjectId, progress: response.data });
        }
      })
    },
    getCourseProgress({ commit }, enrollId) {
      EnrollService.getCourseProgress(enrollId)
      .then(response => {
        if(response.data) {
          commit('SET_COURSE_PROGRESS', response.data);
        }
      })
      .catch(error => { });
    },
    updateLoadingFile({ commit }, data){
      commit('SET_LOADING_FILE', data);
    },

    async fetchTermsOfUseStatus({ commit, state }, { courseId, applicationId }) {
      if (state.termsOfUseStatus[courseId]) return state.termsOfUseStatus[courseId];

      const enrollWithStatus = state.enrolls.find(e => e.courseId === courseId);
      if (enrollWithStatus?.termsOfUseStatus) {
        commit("SET_TERMS_OF_USE_STATUS", { courseId, status: enrollWithStatus.termsOfUseStatus });
        return enrollWithStatus.termsOfUseStatus;
      }

      if (Capacitor.isNativePlatform() && !this.state.dom.isConnected) {
        return { requiresAcceptance: false };
      }

      const { data } = await TermsOfUseService.getStatus(courseId, applicationId);
      commit("SET_TERMS_OF_USE_STATUS", { courseId, status: data });
      return data;
    },

    acceptTermsOfUse({ state, commit }, { applicationUserId, courseId }) {
      commit("SET_TERMS_OF_USE_STATUS", { courseId, status: { requiresAcceptance: false } });
      if (courseId) return;

      state.enrolls
        .filter(enroll =>
          enroll.applicationUserId === applicationUserId &&
          enroll.termsOfUseStatus?.requiresAcceptance &&
          !enroll.termsOfUseStatus?.term?.courseId
        )
        .forEach(enroll =>
          commit("SET_TERMS_OF_USE_STATUS", { courseId: enroll.courseId, status: { requiresAcceptance: false } })
        );
    },
  },
  getters: {
    filtered(state) {
      if (!state.filter) return state.current;

      var filtered = _.cloneDeep(state.current);

      const filterByLObjectTitle = classes => {
        return classes.filter(clss => {
          const filteredLos = clss.learningObjects.filter(lo => {
            return lo.title.toLowerCase().indexOf(state.filter.toLowerCase()) > -1;
          });
          clss.learningObjects = filteredLos;
          return filteredLos.length > 0;
        });
      };

      switch (filtered.structureType.toLowerCase()) {
        case "classes":
          filtered.classes = filterByLObjectTitle(filtered.classes);
          break;

        case "modules":
          for (let i in filtered.modules) {
            filtered.modules[i].classes = filterByLObjectTitle(
              filtered.modules[i].classes
            );
          }

          // se algum modulo não tiver classes, não mostra
          filtered.modules = filtered.modules.filter(mod => {
            return mod.classes.length;
          });

          break;
      }

      return filtered;
    },
    autocomplete(state) {
      if(!state.current) return [];
      var justClassesAndLearningObjs = [];
      const currentEnroll = _.cloneDeep(state.current);
      if(currentEnroll.structureType.toLowerCase() != 'classes') {
        for (let index = 0, l = currentEnroll.modules.length; index < l; index++) {
          justClassesAndLearningObjs = justClassesAndLearningObjs.concat(currentEnroll.modules[index].classes);
        }
      }
      else {
        justClassesAndLearningObjs = currentEnroll.classes;
      }

      return justClassesAndLearningObjs;
    },
    getAll(state) {
      return state.enrolls;
    }
  },
  mutations: {
    SET_OFFLINE_SYNC (state, patch) {
      state.offlineSync = { ...state.offlineSync, ...patch };
    },
    SET_LOADING_FILE (state, data) {
      Vue.set(state.currentLearningObject.files[data.i], 'loadingItem', data.value);
    },
    SET_FULLSCREEN (state, data) {
      state.inFullscreen = data;
    },
    SET_ENROLLS (state, data) { // seta a lista
      state.enrolls = data.items
    },
    SET_ENROLLS_FRESH (state, value) {
      state.enrollsFresh = Boolean(value);
    },
    // Logout / sessão expirada: a lista é do aluno que saiu. O clearToLogin não
    // recarrega a página, e quem procura matrícula na lista (o toque no push,
    // por exemplo) acharia a do anterior em vez de buscar a do novo aluno.
    RESET_ENROLLS (state) {
      state.enrolls = [];
      state.enrollsFresh = false;
    },
    SET_CURRENT_ENROLL (state, { enroll, learningObjects }) { // o selecionado
      Vue.set(state, 'current', enroll);
      state.currentLearningObjects = learningObjects;
      if(enroll.isPreview)
        Vue.set(state, 'isPreview', enroll.isPreview);
    },
    CLEAR_CURRENT(state) {
      state.current = null;
    },
    SET_CURRENT_LEARNING_OBJECT (state, data) {
      state.currentLearningObject = data
    },
    UPDATE_FILTER (state, data) {
      state.filter = data;
    },
    SET_LAST_ACCESS (state, data) {
      state.lastAccess = data;
    },
    SET_PROGRESS (state, data) {
      if (state.currentClass) {
        Vue.set(state.currentClass, 'progress', data);
      }
    },
    SET_OBJ_PROGRESS( state, data) {
      var cls = state.current.classes.find(c=>c.id == data.classId);
      if (!cls) return;
      var learningObject = cls.learningObjects.find(lo => lo.id == data.learningObjectId);
      if (!learningObject) return;

      Vue.set(learningObject, 'progress', data.progress);
    },
    SET_COURSE_PROGRESS(state, data) {
      state.current.progress = data;
    },
    SET_TERMS_OF_USE_STATUS(state, { courseId, status }) {
      Vue.set(state.termsOfUseStatus, courseId, status);
    },
  }
}
